import { useEffect, useMemo, useCallback } from "react";
import { useQuadMediaApiToken } from "@/features/auth/hooks/useApiToken";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { MONITORING_TYPES } from "@/constants/monitoring";
import { toast } from "sonner";
import { useContainerLaunch } from "@/hooks/useContainerLaunch";
import { useContainerData } from "@/hooks/useContainerData";
import { useTokenManagement } from "@/hooks/useTokenManagement";
import type { QuadMediaParams } from "@/features/source-monitoring/api/quadmedia.api";
import { useQuadMediaQuery } from "@/features/source-monitoring/api/quadmedia.queries";
import { useSourceBasedStore } from "@/features/source-monitoring/store/sourceStore";
import { useKeywordsStore } from "@/stores/keywordsStore";
import { TAB_ORDER } from "@/features/source-monitoring/config";

/**
 * Controller hook for SourceContainer
 * Manages all business logic, state, and side effects
 */
export const useSourceContainer = () => {
  const { apiToken, isPending: isApiTokenLoading } = useQuadMediaApiToken();
  const { user } = useAuthStore();
  const { activeTab, setActiveTab, selectedPublications, dateRange, limit } =
    useSourceBasedStore();
  const keywords = useKeywordsStore((s) => s.keywords);

  // Launch management
  const {
    showResults,
    hasLaunched,
    handleShowResults,
    handleLaunch: baseLaunch,
    handlePanelResize,
    setHasLaunched,
  } = useContainerLaunch();

  // Token management
  const {
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    handleTokenSelect,
    trackUsage,
    availableTokens,
    tokensLoading,
  } = useTokenManagement({
    apiToken,
    isApiTokenLoading,
    user,
    showTokenDropdown: true,
    additionalValidation: () => selectedPublications.length > 0,
    monitoringType: "source",
  });

  // Tab navigation
  const currentTabIndex = TAB_ORDER.indexOf(activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === TAB_ORDER.length - 1;

  const handleNext = useCallback(() => {
    if (!isLastTab) setActiveTab(TAB_ORDER[currentTabIndex + 1] as any);
  }, [currentTabIndex, isLastTab, setActiveTab]);

  const handleBack = useCallback(() => {
    if (!isFirstTab) setActiveTab(TAB_ORDER[currentTabIndex - 1] as any);
  }, [currentTabIndex, isFirstTab, setActiveTab]);

  // API params generation
  const getQuadMediaParams = useCallback((): QuadMediaParams => {
    const params: QuadMediaParams = {};

    // Add keywords if present
    const hasKeywords =
      keywords.main.length > 0 ||
      keywords.additional.length > 0 ||
      keywords.excluded.length > 0;

    if (hasKeywords) {
      params.mode = "keyword";
      if (keywords.main.length > 0) {
        params.main_keywords = keywords.main;
      }
      if (keywords.additional.length > 0) {
        params.additional_keywords = keywords.additional;
      }
      if (keywords.excluded.length > 0) {
        params.excluded_keywords = keywords.excluded;
      }
    }

    selectedPublications.forEach((pub) => {
      const idString = pub.id.toString();
      switch (pub.category) {
        case "print":
          if (!params.print_ids) params.print_ids = [];
          params.print_ids.push(idString);
          break;
        case "online":
          if (!params.online_news_ids) params.online_news_ids = [];
          params.online_news_ids.push(idString);
          break;
        case "tv":
          if (!params.tv_ids) params.tv_ids = [];
          params.tv_ids.push(idString);
          break;
        case "radio":
          if (!params.radio_ids) params.radio_ids = [];
          params.radio_ids.push(idString);
          break;
      }
    });
    if (dateRange) {
      params.dateRange = dateRange;
    }
    if (limit !== undefined) {
      params.limit = limit;
    }
    return params;
  }, [selectedPublications, dateRange, limit, keywords]);

  // Data fetching - only enabled when hasLaunched is true
  const { data, isLoading, error, refetch, isFetching } = useQuadMediaQuery(
    getQuadMediaParams(),
    dateRange,
    limit,
    hasLaunched,
  );

  const { responseJSON, setResponseJSON, handleCopyCode } =
    useContainerData(data);

  // Update response JSON when data changes
  useEffect(() => {
    if (data) {
      setResponseJSON(JSON.stringify(data, null, 2));
    }
  }, [data, setResponseJSON]);

  // Error handling
  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  // Track token usage
  useEffect(() => {
    if (data && !isLoading && !isFetching) {
      const count = Array.isArray(data) ? data.length : 0;
      trackUsage(count);
    }
  }, [data, isLoading, isFetching, trackUsage]);

  // API endpoint generation
  const apiEndpoint = useMemo(() => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    if (selectedPublications.length === 0) return "";
    const params = getQuadMediaParams();
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, values]) => {
      if (key === "mode" && typeof values === "string") {
        queryParams.append("mode", values);
      } else if (key === "dateRange" && typeof values === "string") {
        queryParams.append(values, "true");
      } else if (key === "limit" && typeof values === "number") {
        queryParams.append("limit", values.toString());
      } else if (Array.isArray(values)) {
        values.forEach((value) => queryParams.append(key, value));
      }
    });
    return `${baseUrl}quadmedia?${queryParams.toString()}`;
  }, [selectedPublications, getQuadMediaParams]);

  // Results count
  const totalResults = useMemo(() => {
    if (!data || typeof data !== "object") return 0;
    if (Array.isArray(data)) return data.length;

    let count = 0;
    Object.entries(data).forEach(([key, value]) => {
      if (key === "metadata") return;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.values(value).forEach((articles) => {
          if (Array.isArray(articles)) {
            count += articles.length;
          }
        });
      } else if (Array.isArray(value)) {
        count += value.length;
      }
    });
    return count;
  }, [data]);

  // Empty results: metadata.total_articles === 0 (scrape returned no articles)
  const isEmptyResults = useMemo(
    () =>
      !!(
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        data.metadata?.total_articles === 0
      ),
    [data],
  );

  // Launch handler
  const handleLaunch = useCallback(async () => {
    await baseLaunch(refetch);
  }, [baseLaunch, refetch]);

  // Edit sources handler
  const handleEditSources = useCallback(() => {
    handleShowResults(false);
    setHasLaunched(false);
  }, [handleShowResults, setHasLaunched]);

  // Tab label formatter
  const formatTabLabel = useCallback((tab?: string) => {
    if (!tab) return "";
    if (tab.toLowerCase() === "tv") return "TV";
    return tab.charAt(0).toUpperCase() + tab.slice(1);
  }, []);

  return {
    // State
    data,
    isLoading: isLoading || isFetching,
    error,
    showResults,
    hasLaunched,
    isEmptyResults,
    responseJSON,
    apiEndpoint,
    totalResults,
    activeTab,
    currentTabIndex,
    isFirstTab,
    isLastTab,

    // Token
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    apiToken,
    isApiTokenLoading,
    availableTokens,
    tokensLoading,

    // Handlers
    handleShowResults,
    handleLaunch,
    handleCopyCode,
    handleTokenSelect,
    handlePanelResize,
    handleEditSources,
    handleNext,
    handleBack,
    formatTabLabel,

    // Constants
    monitoringType: MONITORING_TYPES.SOURCE,
  };
};
